import React from 'react';
import { graphql, useFragment, useMutation } from 'utils/graphClient';
import {
  chakra,
  HStack,
  IconButton,
  Spacer,
  Text,
  useDisclosure,
} from '@cardboard-ui/react';
import { AnnouncementMetadata_announcement$key } from './__generated__/AnnouncementMetadata_announcement.graphql';
import { UserHandle } from 'components/UserHandle';
import { t } from '@lingui/macro';
import { PinnedItemIcon } from 'components/icons';
import { unpinAnnouncementMutation } from 'components/OptionsMenu/options/PinOption';
import { PinOption_unpinAnnouncement_Mutation } from 'components/OptionsMenu/options/PinOption/__generated__/PinOption_unpinAnnouncement_Mutation.graphql';
import { PinConfirmation } from './PinConfirmation';
import { useI18n } from 'utils/i18n';
import { ItemMenu } from 'components/Item/ItemMenu';
import { Layout, useActiveLayout } from 'utils/LayoutProvider';
import useCurrentTime from 'hooks/useCurrentTime';

const announcementMetadataFragment = graphql`
  fragment AnnouncementMetadata_announcement on Announcement {
    id
    ... on Node {
      ...ItemMenu_node
      ...PinOption_node
    }
    message
    announcer {
      id
      name
      ...UserHandle_member
    }
    publishedAt
    pinned
    canUnpin {
      value
    }
    canPin {
      value
    }
    space {
      modules {
        ... on SpaceAnnouncementsModule {
          canCreateAnnouncement {
            value
          }
        }
      }
    }
    link {
      item {
        ... on Node {
          id
        }
      }
    }
    lastEditedAt
  }
`;

export interface AnnouncementMetadataProps {
  inPinnableContext?: boolean;
  announcement: AnnouncementMetadata_announcement$key;
  announcementsConnectionId: string;
}

const ONE_MINUTE = 60 * 1000;
const THREE_DAYS = 3 * 24 * 60 * 60 * 1000;

export const AnnouncementMetadata = ({
  inPinnableContext,
  announcement,
  announcementsConnectionId,
}: AnnouncementMetadataProps) => {
  const {
    id,
    publishedAt,
    announcer,
    pinned,
    canUnpin,
    space,
    lastEditedAt,
    ...fragments
  } = useFragment(announcementMetadataFragment, announcement);
  const canCreateAnnouncement = !!space.modules.find(
    (m) => !!m.canCreateAnnouncement?.value,
  );
  const {
    isOpen: isConfirmUnpinOpen,
    onOpen: onOpenConfirmUnpin,
    onClose: onCloseConfirmUnpin,
  } = useDisclosure();

  const [unpinAnnouncement, isUnpinLoading] =
    useMutation<PinOption_unpinAnnouncement_Mutation>(
      unpinAnnouncementMutation,
    );
  const isMobile = useActiveLayout() !== Layout.DESKTOP;

  const { formatDistance, format } = useI18n();
  const { now } = useCurrentTime({ refresh: ONE_MINUTE });

  if (!publishedAt || !announcer) {
    return null;
  }

  const publishedAtDate = new Date(publishedAt);

  const isEdited = lastEditedAt && lastEditedAt !== publishedAt;

  const announcementPublishString =
    now - +publishedAtDate > THREE_DAYS
      ? t`on ${format(publishedAtDate, 'PPPp')}`
      : formatDistance(publishedAtDate, now, { addSuffix: true });

  const onUnpin = () => {
    unpinAnnouncement({
      variables: { announcementId: id },
      onCompleted: onCloseConfirmUnpin,
    });
  };

  return (
    <HStack alignItems="flex-start">
      <UserHandle
        member={announcer}
        showAvatar
        avatarProps={{ boxSize: 10 }}
        linkToProfile
        nameProps={{
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 0,
        }}
      >
        <Text fontSize="sm">
          <time dateTime={publishedAtDate.toISOString()}>
            {announcementPublishString}
          </time>
          {isEdited && <chakra.span fontSize="xs"> {t`(edited)`}</chakra.span>}
        </Text>
      </UserHandle>
      <Spacer />
      <HStack spacing={0}>
        {inPinnableContext && pinned && (
          <>
            <IconButton
              icon={<PinnedItemIcon />}
              aria-label={t`Unpin announcement`}
              isDisabled={!canUnpin.value}
              colorScheme="orange"
              variant="ghost"
              size="sm"
              onClick={onOpenConfirmUnpin}
            />
            <PinConfirmation
              isLoading={isUnpinLoading}
              pinned={pinned}
              isOpen={isConfirmUnpinOpen}
              onClose={onCloseConfirmUnpin}
              onConfirmation={onUnpin}
            />
          </>
        )}
        {canCreateAnnouncement && (
          <ItemMenu
            node={fragments}
            mode={isMobile ? 'only-menu' : 'card'}
            connectionId={announcementsConnectionId}
            inPinnableContext={inPinnableContext}
          />
        )}
      </HStack>
    </HStack>
  );
};

export default AnnouncementMetadata;
